import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { exportToCsv } from "../../lib/exportCsv";
import AdminConfirmDialog from "./AdminConfirmDialog";
import BulkActionBar from "./BulkActionBar";
import { FileText, MessageSquare, EyeOff, Eye, Search } from "lucide-react";

const SUBTABS = [
    { id: "adventures", label: "Adventures", icon: FileText },
    { id: "comments", label: "Comments", icon: MessageSquare },
];

const STATUSES = [
    { id: "all", label: "All" },
    { id: "visible", label: "Visible" },
    { id: "hidden", label: "Hidden" },
];

export default function ContentTab() {
    const [sub, setSub] = useState("adventures");

    return (
        <div className="space-y-4">
            <div className="flex gap-1 bg-snowcap border border-ridge-line rounded-lg p-1 w-fit">
                {SUBTABS.map((s) => (
                    <button
                        key={s.id}
                        data-testid={`content-subtab-${s.id}`}
                        onClick={() => setSub(s.id)}
                        className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-md text-xs font-bold transition-all ${
                            sub === s.id
                                ? "bg-midnight-pine text-white"
                                : "text-slate-gray hover:text-granite"
                        }`}
                    >
                        <s.icon className="w-3.5 h-3.5" /> {s.label}
                    </button>
                ))}
            </div>
            {sub === "adventures" ? <AdventuresPanel /> : <CommentsPanel />}
        </div>
    );
}

function AdventuresPanel() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const pageSize = 20;
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState([]);
    const [confirm, setConfirm] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/content/adventures", {
                params: { q: q.trim(), status, page, page_size: pageSize },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setSelected([]);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [q, status, page]);

    useEffect(() => {
        const t = setTimeout(load, q ? 250 : 0);
        return () => clearTimeout(t);
    }, [load, q]);

    useEffect(() => {
        setPage(1);
    }, [q, status]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const toggleAll = () =>
        setSelected(selected.length === items.length ? [] : items.map((i) => i.id));
    const toggleOne = (id) =>
        setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));

    const runBulk = async (action, reason) => {
        try {
            const { data } = await api.post(
                `/admin/content/adventures/bulk?action=${action}`,
                { ids: selected, reason }
            );
            toast.success(`${data.affected} adventures updated`);
            setConfirm(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onExportCsv = () => {
        const rows = selected.length
            ? items.filter((i) => selected.includes(i.id))
            : items;
        exportToCsv(
            rows,
            [
                { key: "id", label: "ID" },
                { key: "title", label: "Title" },
                { key: "author_id", label: "Author ID" },
                { key: "visibility", label: "Visibility" },
                { key: "hidden_by_admin", label: "Hidden", map: (r) => (r.hidden_by_admin ? "yes" : "") },
                { key: "likes_count", label: "Likes" },
                { key: "saves_count", label: "Saves" },
                { key: "comments_count", label: "Comments" },
                { key: "created_at", label: "Created" },
            ],
            "content-adventures"
        );
    };

    return (
        <div className="space-y-4">
            <Filters q={q} setQ={setQ} status={status} setStatus={setStatus} testid="content-adventures" />
            {selected.length > 0 && (
                <BulkActionBar
                    totalVisible={items.length}
                    selectedIds={selected}
                    onToggleSelectAll={toggleAll}
                    onClear={() => setSelected([])}
                    onExportCsv={onExportCsv}
                    testidPrefix="content-adventures-bulk"
                    actions={[
                        {
                            id: "hide",
                            label: "Hide",
                            danger: true,
                            onClick: () => setConfirm({ action: "hide", danger: true, title: `Hide ${selected.length} adventure(s)?`, requireReason: true }),
                        },
                        {
                            id: "restore",
                            label: "Restore",
                            onClick: () => setConfirm({ action: "restore", title: `Restore ${selected.length} adventure(s)?`, requireReason: false }),
                        },
                        {
                            id: "delete",
                            label: "Delete",
                            danger: true,
                            onClick: () => setConfirm({ action: "delete", danger: true, title: `Delete ${selected.length} adventure(s)?`, requireReason: true, description: "Irreversible. Logged to audit trail." }),
                        },
                    ]}
                />
            )}

            <div className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-alpine-mist">
                            <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-gray">
                                <th className="py-3 pl-4 w-10">
                                    <input
                                        type="checkbox"
                                        data-testid="content-adventures-select-all-cb"
                                        checked={items.length > 0 && selected.length === items.length}
                                        onChange={toggleAll}
                                    />
                                </th>
                                <th className="py-3 px-4">Title</th>
                                <th className="py-3 px-4">Author</th>
                                <th className="py-3 px-4">Stats</th>
                                <th className="py-3 px-4">Status</th>
                                <th className="py-3 px-4">Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i} className="border-t border-ridge-line">
                                        <td colSpan={6} className="py-3 px-4">
                                            <div className="h-5 bg-alpine-mist rounded animate-pulse" />
                                        </td>
                                    </tr>
                                ))
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-10 text-center text-slate-gray">
                                        No adventures match these filters.
                                    </td>
                                </tr>
                            ) : (
                                items.map((a) => (
                                    <tr
                                        key={a.id}
                                        data-testid={`content-adventure-row-${a.id}`}
                                        className={`border-t border-ridge-line ${a.hidden_by_admin ? "bg-ember-orange/5" : ""}`}
                                    >
                                        <td className="py-3 pl-4">
                                            <input
                                                type="checkbox"
                                                data-testid={`content-adventure-cb-${a.id}`}
                                                checked={selected.includes(a.id)}
                                                onChange={() => toggleOne(a.id)}
                                            />
                                        </td>
                                        <td className="py-3 px-4 font-semibold text-granite">
                                            <Link
                                                to={`/adventures/${a.id}`}
                                                className="hover:text-ember-orange"
                                            >
                                                {a.title || "(untitled)"}
                                            </Link>
                                        </td>
                                        <td className="py-3 px-4 text-slate-gray text-xs">
                                            {a.author?.name || a.author_id}
                                        </td>
                                        <td className="py-3 px-4 text-xs text-slate-gray">
                                            ♥ {a.likes_count || 0} · 🔖 {a.saves_count || 0}
                                        </td>
                                        <td className="py-3 px-4">
                                            {a.hidden_by_admin ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-ember-orange/20 text-ember-dark">
                                                    <EyeOff className="w-3 h-3" /> Hidden
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-deep-evergreen/15 text-deep-evergreen">
                                                    <Eye className="w-3 h-3" /> Visible
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-xs text-slate-gray">
                                            {a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <Pagination page={page} totalPages={totalPages} setPage={setPage} total={total} items={items.length} pageSize={pageSize} testid="content-adventures" />
            </div>

            <AdminConfirmDialog
                open={!!confirm}
                onClose={() => setConfirm(null)}
                onConfirm={(reason) => runBulk(confirm.action, reason)}
                title={confirm?.title || ""}
                description={confirm?.description || "Logged to the audit trail."}
                confirmLabel="Apply"
                danger={confirm?.danger}
                requireReason={confirm?.requireReason}
            />
        </div>
    );
}

function CommentsPanel() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const pageSize = 20;
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState([]);
    const [confirm, setConfirm] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/content/comments", {
                params: { q: q.trim(), status, page, page_size: pageSize },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setSelected([]);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [q, status, page]);

    useEffect(() => {
        const t = setTimeout(load, q ? 250 : 0);
        return () => clearTimeout(t);
    }, [load, q]);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const toggleAll = () =>
        setSelected(selected.length === items.length ? [] : items.map((i) => i.id));
    const toggleOne = (id) =>
        setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));

    const runBulk = async (action, reason) => {
        try {
            const { data } = await api.post(
                `/admin/content/comments/bulk?action=${action}`,
                { ids: selected, reason }
            );
            toast.success(`${data.affected} comments updated`);
            setConfirm(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onExportCsv = () => {
        const rows = selected.length
            ? items.filter((i) => selected.includes(i.id))
            : items;
        exportToCsv(
            rows,
            [
                { key: "id", label: "ID" },
                { key: "adventure_id", label: "Adventure" },
                { key: "user_id", label: "Author ID" },
                { key: "text", label: "Text" },
                { key: "hidden_by_admin", label: "Hidden", map: (r) => (r.hidden_by_admin ? "yes" : "") },
                { key: "created_at", label: "Created" },
            ],
            "content-comments"
        );
    };

    return (
        <div className="space-y-4">
            <Filters q={q} setQ={setQ} status={status} setStatus={setStatus} testid="content-comments" placeholder="Search comments…" />
            {selected.length > 0 && (
                <BulkActionBar
                    totalVisible={items.length}
                    selectedIds={selected}
                    onToggleSelectAll={toggleAll}
                    onClear={() => setSelected([])}
                    onExportCsv={onExportCsv}
                    testidPrefix="content-comments-bulk"
                    actions={[
                        { id: "hide", label: "Hide", danger: true, onClick: () => setConfirm({ action: "hide", danger: true, title: `Hide ${selected.length} comment(s)?`, requireReason: true }) },
                        { id: "restore", label: "Restore", onClick: () => setConfirm({ action: "restore", title: `Restore ${selected.length} comment(s)?`, requireReason: false }) },
                        { id: "delete", label: "Delete", danger: true, onClick: () => setConfirm({ action: "delete", danger: true, title: `Delete ${selected.length} comment(s)?`, requireReason: true }) },
                    ]}
                />
            )}

            <div className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-alpine-mist">
                            <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-slate-gray">
                                <th className="py-3 pl-4 w-10">
                                    <input
                                        type="checkbox"
                                        data-testid="content-comments-select-all-cb"
                                        checked={items.length > 0 && selected.length === items.length}
                                        onChange={toggleAll}
                                    />
                                </th>
                                <th className="py-3 px-4">Text</th>
                                <th className="py-3 px-4">Adventure</th>
                                <th className="py-3 px-4">Author</th>
                                <th className="py-3 px-4">Status</th>
                                <th className="py-3 px-4">Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="py-8 px-4 text-center text-slate-gray">Loading…</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={6} className="py-10 text-center text-slate-gray">No comments match.</td></tr>
                            ) : (
                                items.map((c) => (
                                    <tr
                                        key={c.id}
                                        data-testid={`content-comment-row-${c.id}`}
                                        className={`border-t border-ridge-line ${c.hidden_by_admin ? "bg-ember-orange/5" : ""}`}
                                    >
                                        <td className="py-3 pl-4">
                                            <input
                                                type="checkbox"
                                                data-testid={`content-comment-cb-${c.id}`}
                                                checked={selected.includes(c.id)}
                                                onChange={() => toggleOne(c.id)}
                                            />
                                        </td>
                                        <td className="py-3 px-4 text-granite line-clamp-2 max-w-md">{c.text || "(empty)"}</td>
                                        <td className="py-3 px-4 text-xs text-slate-gray font-mono truncate max-w-[140px]">{c.adventure_id}</td>
                                        <td className="py-3 px-4 text-xs text-slate-gray font-mono truncate max-w-[140px]">{c.user_id}</td>
                                        <td className="py-3 px-4">
                                            {c.hidden_by_admin ? (
                                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-ember-orange/20 text-ember-dark">Hidden</span>
                                            ) : (
                                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-deep-evergreen/15 text-deep-evergreen">Visible</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-xs text-slate-gray">
                                            {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <Pagination page={page} totalPages={totalPages} setPage={setPage} total={total} items={items.length} pageSize={pageSize} testid="content-comments" />
            </div>

            <AdminConfirmDialog
                open={!!confirm}
                onClose={() => setConfirm(null)}
                onConfirm={(reason) => runBulk(confirm.action, reason)}
                title={confirm?.title || ""}
                confirmLabel="Apply"
                danger={confirm?.danger}
                requireReason={confirm?.requireReason}
            />
        </div>
    );
}

function Filters({ q, setQ, status, setStatus, testid, placeholder }) {
    return (
        <div className="bg-snowcap border border-ridge-line rounded-lg p-4 space-y-3">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                <input
                    data-testid={`${testid}-search`}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={placeholder || "Search by title, ID, or user…"}
                    className="w-full pl-10 pr-4 py-2.5 rounded-md bg-alpine-mist border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20 text-sm"
                />
            </div>
            <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                    <button
                        key={s.id}
                        data-testid={`${testid}-status-${s.id}`}
                        onClick={() => setStatus(s.id)}
                        className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest transition-all ${
                            status === s.id ? "bg-midnight-pine text-white" : "bg-alpine-mist text-slate-gray hover:text-granite"
                        }`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function Pagination({ page, totalPages, setPage, total, items, pageSize, testid }) {
    return (
        <div className="flex items-center justify-between p-4 border-t border-ridge-line">
            <span className="text-xs text-slate-gray font-semibold">
                Showing {items === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + items} of {total}
            </span>
            <div className="flex items-center gap-2">
                <button
                    data-testid={`${testid}-prev`}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40 hover:border-midnight-pine"
                >
                    Prev
                </button>
                <span className="text-xs font-bold text-granite">{page} / {totalPages}</span>
                <button
                    data-testid={`${testid}-next`}
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="px-3 py-1.5 rounded-md text-xs font-bold border border-ridge-line disabled:opacity-40 hover:border-midnight-pine"
                >
                    Next
                </button>
            </div>
        </div>
    );
}
